/**
 * useTGDataTable Hook
 * Manages query state, filters, pagination, column visibility, and column order
 * Persists to localStorage when presetKey is provided
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * @param {Object} options
 * @param {string} [options.presetKey] - LocalStorage key for persistence
 * @param {number} [options.defaultPageSize] - Default page size
 * @param {string[]} [options.defaultVisibleColumns] - Default visible columns
 * @param {string[]} [options.defaultColumnOrder] - Default column order (keys)
 * @returns {Object} Query state and setters
 */
function useTGDataTable({
  presetKey,
  defaultPageSize = 10,
  defaultVisibleColumns = [],
  defaultColumnOrder = [],
} = {}) {
  // Load initial state from localStorage if presetKey provided
  const loadStoredState = useCallback(() => {
    if (!presetKey) return null;
    try {
      const stored = localStorage.getItem(presetKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to load table preset:', error);
      return null;
    }
  }, [presetKey]);

  const storedState = useMemo(() => loadStoredState(), [loadStoredState]);

  // Query state
  const [page, setPage] = useState(storedState?.page || 1);
  const [pageSize, setPageSize] = useState(storedState?.pageSize || defaultPageSize);
  const [sort, setSort] = useState(storedState?.sort || null);
  const [filters, setFilters] = useState(storedState?.filters || {});
  const [visibleColumns, setVisibleColumns] = useState(
    storedState?.visibleColumns || defaultVisibleColumns
  );
  const [columnOrder, setColumnOrder] = useState(
    storedState?.columnOrder || defaultColumnOrder
  );

  // Persist state to localStorage
  useEffect(() => {
    if (!presetKey) return;

    const state = {
      page,
      pageSize,
      sort,
      filters,
      visibleColumns,
      columnOrder,
    };

    try {
      localStorage.setItem(presetKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save table preset:', error);
    }
  }, [presetKey, page, pageSize, sort, filters, visibleColumns, columnOrder]);

  // Query state object
  const query = useMemo(
    () => ({
      page,
      pageSize,
      sort,
      filters,
      visibleColumns,
      columnOrder,
    }),
    [page, pageSize, sort, filters, visibleColumns, columnOrder]
  );

  // Set single filter
  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      // Remove filter if value is null/undefined/empty
      if (value === null || value === undefined || value === '') {
        const { [key]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
    // Reset to page 1 when filter changes
    setPage(1);
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  // Reset to defaults
  const reset = useCallback(() => {
    setPage(1);
    setPageSize(defaultPageSize);
    setSort(null);
    setFilters({});
    setVisibleColumns(defaultVisibleColumns);
    setColumnOrder(defaultColumnOrder);
    if (presetKey) {
      localStorage.removeItem(presetKey);
    }
  }, [presetKey, defaultPageSize, defaultVisibleColumns, defaultColumnOrder]);

  // Toggle column visibility
  const toggleColumn = useCallback((columnKey) => {
    setVisibleColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey]
    );
  }, []);

  // Set all visible columns
  const setAllColumns = useCallback((keys) => {
    setVisibleColumns(keys);
  }, []);

  // Reorder columns
  const reorderColumns = useCallback((fromIndex, toIndex) => {
    setColumnOrder((prev) => {
      const newOrder = [...prev];
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      return newOrder;
    });
  }, []);

  return {
    query,
    page,
    pageSize,
    sort,
    filters,
    visibleColumns,
    columnOrder,
    setPage,
    setPageSize,
    setSort,
    setFilter,
    setFilters,
    clearFilters,
    setVisibleColumns,
    setColumnOrder,
    toggleColumn,
    setAllColumns,
    reorderColumns,
    reset,
  };
}

export default useTGDataTable;
